import { useCallback, useEffect, useState } from 'react';
import type { ShiftOrderRow } from '@dastarhan/contracts';
import { formatMoney, moneyInWords } from '@dastarhan/core';
import { receiptLines } from '@dastarhan/core';
import { api } from '../api';
import { pendingChecks, rejectedChecks } from '../offline/queue';
import { printSmart } from '../print';
import { ElevateDialog } from './ElevateDialog';

const METHOD_RU: Record<string, string> = { CASH: 'нал', CARD: 'карта', KASPI_QR: 'QR' };
const REASONS = ['Ошибка кассира', 'Отказ гостя', 'Не то блюдо'] as const;
const FISCAL_RU: Record<string, { text: string; cls: string }> = {
  SENT: { text: 'ФН ✓', cls: 'ok' },
  QUEUED: { text: 'в ОФД…', cls: '' },
  ERROR: { text: 'ФН ошибка', cls: 'err' },
  OFF: { text: 'фиск. выкл', cls: 'dim' },
};
const FISCAL_REFUND_RU: Record<string, { text: string; cls: string }> = {
  SENT: { text: 'возврат ФН ✓', cls: 'ok' },
  QUEUED: { text: 'возврат в ОФД…', cls: '' },
  ERROR: { text: 'возврат: ошибка', cls: 'err' },
  OFF: { text: 'без ФН', cls: 'dim' },
};

/**
 * Чеки текущей смены. Возврат: причина → PIN менеджера → событие
 * order.cancelled. Требует связи — PIN проверяет сервер.
 */
export function ChecksScreen({ token, shiftId, online, onBack, onCancelOrder }: {
  token: string;
  shiftId: string;
  online: boolean;
  onBack: () => void;
  onCancelOrder: (orderId: string, reason: string, approver: string, reopen: boolean) => Promise<void>;
}) {
  const [rows, setRows] = useState<ShiftOrderRow[] | null>(null);
  // Печать копии по id: и строке списка, и кнопке «последний чек»
  const reprint = (orderId: string) => {
    void api.orderDetail(token, orderId).then((d) => {
      void printSmart(receiptLines({
        place: localStorage.getItem('dastarhan.place') ?? 'Dastarhan',
        number: d.number,
        cashier: d.cashierName,
        ...(d.tableName ? { table: d.tableName } : {}),
        copy: true,
        at: new Date(d.closedAt),
        items: d.items,
        total: d.total,
        payments: d.payments.map((p) => ({
          method: p.method, amount: p.amount,
          ...(p.tenderedCash !== null ? { tenderedCash: p.tenderedCash } : {}),
        })),
        ...(d.fiscal.qrUrl ? { fiscalUrl: d.fiscal.qrUrl } : {}),
      }), d.fiscal.qrUrl ?? undefined);
    }).catch(() => null);
  };
  const [failed, setFailed] = useState(false);
  const [target, setTarget] = useState<ShiftOrderRow | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  // Ошибка по товару — не отказ гостя: он не уходит, ему нужно исправить
  // состав. Тогда заказ возвращается официанту вместо простого закрытия
  const [reopen, setReopen] = useState(false);

  // Открытые счета: официант напечатал счёт, гость ещё не заплатил.
  // В чеках смены их нет и быть не может — это не продажа, пока деньги
  // не получены. Но официант ищет их именно здесь и должен находить
  // Чеки, ещё не ушедшие на сервер. Без них при офлайне экран пуст,
  // хотя кассир пробил двадцать чеков — для него это выглядит
  // как потеря денег
  const [pending, setPending] = useState<{
    orderId: string; number: number; total: number; at: string;
  }[]>([]);
  // Чеки, которые сервер не принял. Деньги получены, а записи нет —
  // это надо видеть, а не узнавать через месяц из недостачи
  const [rejected, setRejected] = useState<{
    number: number; total: number; reason: string; at: string;
  }[]>([]);

  const [openRows, setOpenRows] = useState<{
    orderId: string; tableName: string; openedByName: string;
    awaitingPay: boolean; sum: number;
  }[]>([]);

  const load = useCallback(() => {
    setFailed(false);
    api.shiftOrders(token, shiftId)
      .then((r) => setRows(r.orders))
      .catch(() => { setRows([]); setFailed(true); });
    void pendingChecks().then(setPending).catch(() => undefined);
    void rejectedChecks().then(setRejected).catch(() => undefined);
    void api.openOrders(token)
      .then((d) => setOpenRows(d.rows.map((r) => ({
        orderId: r.orderId, tableName: r.tableName,
        openedByName: r.openedByName, awaitingPay: r.awaitingPay,
        sum: r.items.reduce((acc, it) => acc + Math.round(it.qty * it.unitPrice), 0) - r.discount,
      }))))
      .catch(() => undefined);
  }, [token, shiftId]);
  useEffect(load, [load]);

  return (
    <div className="pay checks">
      <button className="btn ghost" style={{ alignSelf: 'flex-start' }} onClick={onBack}>← К заказу</button>
      <h1>Чеки смены</h1>

      {/* Незакрытые счета первыми: официант закрыл счёт, гость ещё
          не заплатил — и ищет он именно это. В чеках смены их не будет,
          пока деньги не получены: выручка считается по оплате */}
      {openRows.length > 0 && (
        <>
          <h2 className="checks-group">
            Незакрытые счета · {openRows.length}
          </h2>
          <div className="checks-open">
            {openRows.map((r) => (
              <div key={r.orderId} className={`check-open ${r.awaitingPay ? 'waiting' : ''}`}>
                <b>{r.tableName}</b>
                <span className="co-who">{r.openedByName}</span>
                <span className="co-state">
                  {r.awaitingPay ? 'ждёт оплаты' : 'в работе'}
                </span>
                <span className="co-sum money">{formatMoney(r.sum)}</span>
              </div>
            ))}
          </div>
          <h2 className="checks-group">Оплаченные</h2>
        </>
      )}

      {rows === null && <p className="hint">Загружаем…</p>}
      {/* Непринятые сервером — САМОЕ ВЕРХНЕЕ: деньги получены,
          а записи нет. Молчать об этом нельзя ни минуты */}
      {rejected.length > 0 && (
        <>
          <h2 className="checks-group bad">
            Сервер не принял · {rejected.length}
          </h2>
          <div className="checks-open">
            {rejected.map((r, i) => (
              <div key={i} className="check-open rejected">
                <b>№{r.number}</b>
                <span className="co-who">
                  {new Date(r.at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="co-state">{r.reason}</span>
                <span className="co-sum money">{formatMoney(r.total)}</span>
              </div>
            ))}
          </div>
          <p className="hint">
            Эти чеки пробиты, деньги получены, но сервер их не записал.
            Покажите этот экран владельцу — суммы не потеряны,
            их нужно провести вручную.
          </p>
        </>
      )}

      {/* Неотправленные — с объяснением: кассир видел эти суммы
          своими глазами, и пустой экран его напугает */}
      {pending.length > 0 && (
        <>
          <h2 className="checks-group">
            Ждут отправки · {pending.length}
          </h2>
          <div className="checks-open">
            {pending.map((r) => (
              <div key={r.orderId} className="check-open pending">
                <b>№{r.number}</b>
                <span className="co-who">
                  {new Date(r.at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="co-state">на устройстве</span>
                <span className="co-sum money">{formatMoney(r.total)}</span>
              </div>
            ))}
          </div>
          <p className="hint">
            Эти чеки пробиты и сохранены здесь. Они уйдут на сервер,
            когда вернётся связь — ничего не потеряется.
          </p>
        </>
      )}

      {failed && <p className="hint">Список недоступен без связи. Возврат тоже требует сети — PIN менеджера проверяет сервер.</p>}
      {rows !== null && rows.length > 0 && (
        <button className="btn" style={{ marginBottom: 10 }} disabled={!online}
          title="Копия самого свежего чека — гость вернулся за бумагой"
          onClick={() => {
            const last = rows.find((x) => x.status !== 'CANCELLED');
            if (last) reprint(last.orderId);
          }}>
          ↻ Последний чек — копия
        </button>
      )}
      {rows !== null && !failed && rows.length === 0 && (
        <p className="hint">Пока ни одного чека. Первый появится здесь после оплаты.</p>
      )}

      <div className="check-list">
        {(rows ?? []).map((o) => (
          <div key={o.orderId} className={`check-row ${o.status === 'CANCELLED' ? 'cancelled' : ''}`}>
            <b>№{o.number}</b>
            <span className="at">{new Date(o.closedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
            <span className="mth">
              {o.tableName ? `${o.tableName} · ` : ''}
              {o.methods.map((m) => METHOD_RU[m] ?? m).join(' + ')}
              {' '}{(() => {
                const f = (o.status === 'CANCELLED' ? FISCAL_REFUND_RU : FISCAL_RU)[o.fiscal];
                return <i className={`fiscal ${f?.cls ?? ''}`}>{f?.text}</i>;
              })()}
            </span>
            <span className="money">{formatMoney(o.total)}</span>
            {o.status === 'CANCELLED'
              ? <span className="tag">возврат</span>
              : (<>
                <button className="btn small" disabled={!online} title="Печать копии"
                  onClick={() => reprint(o.orderId)}>
                  Копия
                </button>
                <button className="btn small danger" disabled={!online}
                  onClick={() => { setTarget(o); setReason(null); }}>
                  Вернуть
                </button>
              </>)}

          </div>
        ))}
      </div>

      {target && reason === null && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Причина возврата">
          <div className="dialog">
            <span className="eyebrow">Возврат</span>
            <h1>Возврат чека №{target.number}</h1>
            <p className="hint">
              К возврату: <b>{formatMoney(target.total)}</b>
              {' '}({moneyInWords(target.total)})
            </p>
            <p className="hint">Почему возвращаем? Причина ляжет в отчёт владельца.</p>
            {(() => {
              // недавняя причина всплывает первой: смена повторяет ошибки сериями
              let recent: string[] = [];
              try { recent = JSON.parse(localStorage.getItem('dastarhan.recentReasons') ?? '[]') as string[]; }
              catch { recent = []; }
              const ordered = [...recent.filter((r) => (REASONS as readonly string[]).includes(r)),
                ...REASONS.filter((r) => !recent.includes(r))];
              return ordered.map((r) => (
                <button key={r} className="btn" style={{ width: 240 }}
                  onClick={() => {
                    const next = [r, ...recent.filter((x) => x !== r)].slice(0, 3);
                    localStorage.setItem('dastarhan.recentReasons', JSON.stringify(next));
                    setReason(r);
                  }}>{r}</button>
              ));
            })()}
            <label className="reopen-check">
              <input type="checkbox" checked={reopen}
                onChange={(e) => setReopen(e.target.checked)} />
              Вернуть заказ официанту для исправления
            </label>
            <p className="hint">
              Отметьте, если гость остаётся: ошиблись в блюде, нужно заменить
              или добавить. Стол снова откроется с теми же позициями.
            </p>
            <div className="dialog-actions">
              <button className="btn ghost" onClick={() => setTarget(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {target && reason !== null && (
        <ElevateDialog title={`Возврат №${target.number} · ${formatMoney(target.total)}`}
          onCancel={() => { setTarget(null); setReason(null); }}
          onConfirm={async (pin) => {
            try {
              const r = await api.elevate(token, { pin, action: 'order.refund' });
              await onCancelOrder(target.orderId, reason, r.approver, reopen);
              setTarget(null); setReason(null); setReopen(false);
              load();
              return r.approver;
            } catch { return null; }
          }}
        />
      )}
    </div>
  );
}
